/**
 * One read of `dealbid-core`, with the three states a section has to render.
 *
 * Canon `docs/TESTING.md` § UI component/integration names loading, empty, error and data
 * as separately failable behaviour, so they are modelled explicitly here rather than being
 * inferred from `data === undefined`.
 *
 * A ready state also records **when** the response arrived. Countdowns are measured against
 * core's clock (canon § 8), and the offset between the two clocks is only computable if the
 * local instant of arrival is kept alongside the server time that came with it.
 *
 * Optional polling covers the auction section: canon `AGENTS.md` § 5 allows polling for
 * auction UI in V0 and rules out WebSockets. A refresh deliberately does **not** drop back
 * to the loading state — a board that blanked every minute would be worse than one that
 * updates in place.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";

export type Loader<T> = (signal?: AbortSignal) => Promise<T>;

export type ResourceState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T; receivedAtMs: number }
  | { status: "error"; error: ApiError };

export interface Resource<T> {
  state: ResourceState<T>;
  /** Retry after a failure. Returns to the loading state, so the retry is visible. */
  reload: () => void;
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError("UNEXPECTED", "Dealbid couldn't load this section.");
}

export function useApiResource<T>(
  load: Loader<T>,
  options: { refreshMs?: number | undefined } = {},
): Resource<T> {
  const { refreshMs } = options;
  const [state, setState] = useState<ResourceState<T>>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => {
    // The loading state is entered here, in the event handler, rather than inside the
    // effect below: a retry is something the visitor did, and the effect's job is only to
    // run the request that follows from it.
    setState({ status: "loading" });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void load(controller.signal)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data, receivedAtMs: Date.now() });
      })
      .catch((error: unknown) => {
        // An abort is this effect being torn down — by an unmount, or by React 19's
        // StrictMode running effects twice in development. It is not a visitor-facing
        // failure and must not replace a good render with an error.
        if (cancelled || isAbort(error)) return;
        setState({ status: "error", error: asApiError(error) });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [load, attempt]);

  useEffect(() => {
    if (refreshMs === undefined) return undefined;

    const timer = setInterval(() => {
      // A hidden tab costs the visitor battery and core requests for a board nobody is
      // looking at. The next visible tick catches up.
      if (document.visibilityState === "hidden") return;

      void load()
        .then((data) => {
          setState({ status: "ready", data, receivedAtMs: Date.now() });
        })
        .catch(() => {
          // A background refresh that fails leaves the last good data on screen. The
          // visitor did not ask for this request, so a transient blip must not turn a
          // working section into an error panel; the next tick tries again.
        });
    }, refreshMs);

    return () => {
      clearInterval(timer);
    };
  }, [load, refreshMs]);

  return { state, reload };
}
